import { FC, useMemo } from 'react';

import { SquarePen, Trash2 } from 'lucide-react';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import css from './SpecialDropsTable.module.css';

import { DataTable } from 'v2/components/dataTable/DataTable';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { TableColumn } from 'v2/types';
import { SpecialDropModel } from 'v2/types/SpecialDropModel';

interface SpecialDropsTableProps {
  specialDrops: SpecialDropModel[];
  onEdit: (specialDropId: string) => void;
  onDelete: (specialDropId: string) => void;
}

const getSentRewardsMessage = (rewardsData: Record<string, boolean>): string => {
  const statuses = Object.values(rewardsData ?? {});
  const sentCount = statuses.filter(Boolean).length;

  return `${sentCount} of ${statuses.length}`;
};

export const SpecialDropsTable: FC<SpecialDropsTableProps> = ({ specialDrops, onEdit, onDelete }) => {
  const columns = useMemo<TableColumn<SpecialDropModel>[]>(
    () => [
      { key: 'type', label: 'Type', render: (row) => row.type },
      { key: 'rollCount', label: 'Roll Count', render: (row) => row.rollCount },
      { key: 'chanceToWin', label: 'Chance To Win', render: (row) => `${row.chanceToWin}%` },
      { key: 'rewardsSent', label: 'Rewards sent', render: (row) => getSentRewardsMessage(row.rewardsData) },
      {
        key: 'actions',
        label: 'Actions',
        align: 'right',
        render: (row) => (
          <div className={css.specialDropsTableActions}>
            <Tooltip title="Edit" placement="top">
              <IconButton onClick={() => onEdit(row.id)}>
                <SquarePen width={20} height={20} />
              </IconButton>
            </Tooltip>

            <Popconfirm
              message="Are you sure that you want to delete this special drop?"
              onConfirm={() => onDelete(row.id)}
            >
              <Tooltip title="Delete" placement="top">
                <IconButton color="error">
                  <Trash2 width={20} height={20} />
                </IconButton>
              </Tooltip>
            </Popconfirm>
          </div>
        ),
      },
    ],
    [onEdit, onDelete]
  );

  return <DataTable columns={columns} data={specialDrops} />;
};
